import { type AdminDropDto, type VoucherState, voucherState } from '@pulsify/shared'
import { useDailyDropStats } from '~/entities/drop/model/use-drops'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { Badge } from '~/shared/ui/badge'

const VOUCHER_STATE_LABELS: Record<VoucherState, () => string> = {
  open: m.dropVoucherOpen,
  redeemed: m.dropVoucherRedeemed,
  expired: m.dropVoucherExpired,
}

const VOUCHER_STATE_VARIANTS: Record<VoucherState, 'default' | 'secondary' | 'outline'> = {
  open: 'secondary',
  redeemed: 'default',
  expired: 'outline',
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(getLocale(), { dateStyle: 'medium', timeStyle: 'short' })
}

/** A running daily drop's numbers and its winners with their vouchers. */
export function DailyDropPanel({ drop }: { drop: AdminDropDto }) {
  const { data: stats } = useDailyDropStats(drop.id)
  const now = new Date()
  const tiles = [
    { label: m.dropDailyAttemptsToday(), value: stats?.attemptsToday },
    { label: m.dropDailyAttemptsTotal(), value: stats?.attemptsTotal },
    { label: m.dropDailyPlayers(), value: stats?.players },
    { label: m.dropDailyPrizesWon(), value: stats?.prizesWon ?? drop.prizesWon },
    {
      label: m.dropDailyRemainingLabel(),
      value: stats?.remaining ?? Math.max(0, (drop.prizeContingent ?? 0) - drop.prizesWon),
    },
    { label: m.dropDailyRedeemed(), value: stats?.vouchersRedeemed },
  ]

  return (
    <div className="space-y-4">
      <div className="gap-3 grid grid-cols-2 sm:grid-cols-3">
        {tiles.map((tile) => (
          <div
            key={tile.label}
            className="p-3 border border-slate-200 dark:border-slate-800 rounded-lg"
          >
            <p className="text-slate-500 dark:text-slate-400 text-xs">{tile.label}</p>
            <p className="font-semibold text-slate-900 dark:text-slate-100 text-xl tabular-nums">
              {tile.value ?? '…'}
            </p>
          </div>
        ))}
      </div>
      <div>
        <h3 className="mb-2 font-semibold text-slate-900 dark:text-slate-100">
          {m.dropDailyWinnersTitle()}
        </h3>
        {(stats?.winners.length ?? 0) === 0 ? (
          <p className="text-slate-600 dark:text-slate-400 text-sm">{m.dropDailyNoWinners()}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-slate-200 dark:border-slate-800 border-b text-left text-slate-500 dark:text-slate-400">
                  <th className="px-2 py-2 font-medium">{m.dropUserCol()}</th>
                  <th className="px-2 py-2 font-medium">{m.dropWonAt()}</th>
                  <th className="px-2 py-2 font-medium">{m.dropVoucherCode()}</th>
                  <th className="px-2 py-2 font-medium">{m.dropVoucherValidUntil()}</th>
                  <th className="px-2 py-2 font-medium">{m.dropVoucherState()}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {stats?.winners.map((winner) => {
                  const state = voucherState(winner, now)
                  return (
                    <tr key={winner.userId}>
                      <td className="px-2 py-2 text-slate-900 dark:text-slate-100">
                        {winner.displayName ?? winner.userId}
                      </td>
                      <td className="px-2 py-2 text-slate-600 dark:text-slate-400 whitespace-nowrap">
                        {formatDateTime(winner.wonAt)}
                      </td>
                      <td className="px-2 py-2 font-mono text-slate-900 dark:text-slate-100">
                        {winner.code}
                      </td>
                      <td className="px-2 py-2 text-slate-600 dark:text-slate-400 whitespace-nowrap">
                        {formatDateTime(winner.validUntil)}
                      </td>
                      <td className="px-2 py-2">
                        <Badge variant={VOUCHER_STATE_VARIANTS[state]}>
                          {VOUCHER_STATE_LABELS[state]()}
                        </Badge>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
